import React from "react";
import { AssetSelect, Button, Text, Tooltip } from "@showitinc/showit-wc";

import UIActions from "../../../actions/UIActions";
import PexelsSelectHandler from "../../../components/utils/PexelsSelectHandler";
import AssetUtils from "../../../utils/AssetUtils";
import { getAssetFileNameFromKey } from "../../../utils/AssetNameUtils";
import useUserAssets from "../../../hooks/query/useUserAssets";
import { createPexelsElementContent } from "../../../utils/PexelsUtils";
import AppConstants from "../../../constants/AppConstants";
import HelpTooltip from "../../../constants/HelpTooltips";
import styles from "./AssetSelectInput.module.css";

const {
  ASSET_LIBRARY_MODE_CHOOSER,
  ASSET_TYPE_IMAGE,
  ASSET_TYPE_LOTTIE,
  ASSET_TYPE_VIDEO,
  ELEMENT_TYPE_LOTTIE,
  UPDATE_ACTION_SET,
} = AppConstants;

/**
 * Picks one asset from the media library into `propName`.
 * @param {Object} props
 * @param {string} [props.assetType="image"]
 * @param {boolean} [props.allowStockPhotos=false] offer the Stock Photos tab for image assets
 */
const AssetSelectInput = ({
  allowStockPhotos = false,
  assetType = "image",
  children,
  className,
  fullWidth,
  disabled,
  enableDelete = false,
  extensionFilter = false,
  helpTooltip,
  hideTooltip = false,
  label,
  longLabel = false,
  onChange,
  onSelect,
  propName,
  value,
}) => {
  const { assets } = useUserAssets();

  const _updateSelection = (asset) => {
    if (onChange) {
      const propInfo = {
        name: propName,
        value: asset,
        origValue: value,
      };

      // SET, not the default merge, so a new asset doesn't inherit the old one's
      // provider or attribution. Clearing keeps the default, which deletes the key.
      if (asset) propInfo.action = UPDATE_ACTION_SET;

      onChange(propInfo);
    }
  };

  const _setAsset = (asset) => {
    // remove information that is not needed for rendering
    _updateSelection(AssetUtils.getElementAsset(asset));
  };

  const _setStockPhoto = (photo) => {
    _updateSelection(createPexelsElementContent(photo));
    PexelsSelectHandler.recordUse(photo);
  };

  const _deleteAsset = () => _updateSelection(null);

  const _selectAsset = () => {
    const config = {
      btnLabel: "Select",
      onSelect: _setAsset,
      mode: ASSET_LIBRARY_MODE_CHOOSER,
    };

    switch (assetType) {
      case "video":
        config.title = "Select Video";
        config.filter = ASSET_TYPE_VIDEO;
        break;
      case ELEMENT_TYPE_LOTTIE:
        config.title = "Select Lottie";
        config.filter = ASSET_TYPE_LOTTIE;
        break;
      case "favicon":
        config.title = "Select Favicon";
        config.filterExts = ["png", "ico"];
        break;
      case "image":
      default:
        config.title = "Select Image";
        config.filter = ASSET_TYPE_IMAGE;
        if (allowStockPhotos) config.stockPhotoOnSelect = _setStockPhoto;
        break;
    }

    if (extensionFilter) {
      config.filterExts = extensionFilter;
    }

    UIActions.toggleAssetChooser(config);
  };

  const clickHandler = !disabled ? onSelect || _selectAsset : null;

  const key = value ? value.get("key") : "";
  const provider = value ? value.get("provider") : undefined;
  const assetLabel = key ? getAssetFileNameFromKey(assets, key) : `Choose ${assetType}`;
  const view = (
    <AssetSelect
      fullWidth={fullWidth}
      reserveContext
      hideTooltip={hideTooltip}
      className={className}
      disabled={disabled}
      label={label}
      longLabel={longLabel}
      onSIAssetClick={clickHandler}
      previewUrl={key && AssetUtils.getV2PreviewURL(key, { provider })}
      value={assetLabel}
    >
      {children}
      {enableDelete && value && (
        <Button
          className={styles["delete-button"]}
          slot="suffix"
          variant="icon"
          iconPrefix="trash"
          size="20"
          onSIClick={_deleteAsset}
        />
      )}
    </AssetSelect>
  );

  return helpTooltip ? (
    <Tooltip position="bottom-left">
      {view}
      <Text slot="content" dangerouslySetInnerHTML={{ __html: HelpTooltip[helpTooltip]?.t }} />
    </Tooltip>
  ) : (
    view
  );
};

export default AssetSelectInput;
